import dayjs, { Dayjs } from "dayjs"
import { LEVEL_NAME_SEPARATOR } from "./constants"
import { OpenCellSchedule, OpenCellScheduleRow, Weekday } from "./types"
import {
   AlarmScheduleResponse,
   AlarmScheduleWindow,
} from "@/api/AlarmSchedulesService"
import { FacilityDetails } from "@/types/facility"
import { buildLevelSelectors, getLevelLineage } from "@/utils/facilities"
import { getLocale, isValidTimezone } from "@/utils/index"

export const TIME_FORMAT = "HH:mm"
export const TWELVE_HOUR_FORMAT = "hh:mm A"

const NOON_HOUR = 12

const isUSLocale = (): boolean => getLocale().startsWith("en-US")

// The US reads times on a 12-hour clock; everyone else on a 24-hour one.
export const uses12HourClock = (): boolean => isUSLocale()

// The US week starts on Sunday; everywhere else it starts on Monday.
export const startsWeekOnSunday = (): boolean => isUSLocale()

export const WEEKDAY_LABELS: Record<Weekday, string> = {
   [Weekday.SUNDAY]: "Sun",
   [Weekday.MONDAY]: "Mon",
   [Weekday.TUESDAY]: "Tue",
   [Weekday.WEDNESDAY]: "Wed",
   [Weekday.THURSDAY]: "Thu",
   [Weekday.FRIDAY]: "Fri",
   [Weekday.SATURDAY]: "Sat",
}

export const WEEKDAYS_IN_ORDER = Object.keys(WEEKDAY_LABELS) as Weekday[]

export const getDisplayedWeekdays = (): Weekday[] => {
   if (startsWeekOnSunday()) return WEEKDAYS_IN_ORDER

   const [sunday, ...rest] = WEEKDAYS_IN_ORDER

   return [...rest, sunday]
}

export const formatTime = (time: string, use12Hour: boolean): string => {
   if (!use12Hour) return time

   const [hours, minutes] = time.split(":").map(Number)
   const period = hours < NOON_HOUR ? "AM" : "PM"
   const twelveHour = hours % NOON_HOUR || NOON_HOUR

   return `${twelveHour}:${String(minutes).padStart(2, "0")} ${period}`
}

export const formatTimeInterval = (
   startTime: string,
   endTime: string,
   use12Hour: boolean
): string =>
   `${formatTime(startTime, use12Hour)} - ${formatTime(endTime, use12Hour)}`

export const parseTime = (time: string): Dayjs | null => {
   if (!time) return null

   const [hours, minutes] = time.split(":").map(Number)

   return dayjs().hour(hours).minute(minutes).second(0).millisecond(0)
}

type ScheduleSlot = Pick<
   OpenCellSchedule,
   "levelId" | "startTime" | "endTime" | "weekdays"
>

// "HH:mm" strings are zero-padded, so comparing them as text orders them by time.
export const schedulesOverlap = (a: ScheduleSlot, b: ScheduleSlot): boolean => {
   if (a.levelId !== b.levelId) return false

   const sharesWeekday = a.weekdays.some((weekday) =>
      b.weekdays.includes(weekday)
   )

   return sharesWeekday && a.startTime < b.endTime && b.startTime < a.endTime
}

export const isScheduleValid = (schedule: ScheduleSlot): boolean =>
   Boolean(schedule.levelId) &&
   Boolean(schedule.startTime) &&
   Boolean(schedule.endTime) &&
   schedule.startTime < schedule.endTime &&
   schedule.weekdays.length > 0

type FacilityLevels = FacilityDetails["levels"]

const getActiveValuesByType = (
   levels: FacilityLevels,
   levelId: string
): Record<string, string> =>
   Object.fromEntries(
      getLevelLineage(levels, levelId).map((level) => [level.type.id, level.id])
   )

export const getLevelSelectorsState = (
   levelTypes: FacilityDetails["levelTypes"],
   levels: FacilityLevels,
   levelId: string,
   overrides: Record<string, string> = {}
) => {
   const selectors = buildLevelSelectors(levelTypes, levels, {
      ...getActiveValuesByType(levels, levelId),
      ...overrides,
   })
   const floorSelector = selectors[selectors.length - 1]
   const levelName = selectors
      .map(
         (selector) =>
            selector.options.find(
               (option) => option.value === selector.activeValue
            )?.label
      )
      .filter(Boolean)
      .join(LEVEL_NAME_SEPARATOR)

   return {
      selectors,
      levelId: floorSelector?.activeValue ?? "",
      levelName,
      levelIcon: floorSelector?.icon,
   }
}

export const API_WEEKDAYS = [
   Weekday.MONDAY,
   Weekday.TUESDAY,
   Weekday.WEDNESDAY,
   Weekday.THURSDAY,
   Weekday.FRIDAY,
   Weekday.SATURDAY,
   Weekday.SUNDAY,
]

export const fromApiTime = (time: string): string =>
   time.slice(0, TIME_FORMAT.length)

// Merges a floor's windows with the same times into one row.
export const schedulesFromAlarmSchedules = (
   alarmSchedules: AlarmScheduleResponse[]
): OpenCellSchedule[] =>
   alarmSchedules.flatMap(({ level, windows }) => {
      const schedulesByTime = new Map<string, OpenCellSchedule>()

      windows.forEach(({ id, weekday, startTime, endTime }) => {
         const timeKey = `${startTime}-${endTime}`
         const schedule = schedulesByTime.get(timeKey) ?? {
            id,
            levelId: level,
            startTime: fromApiTime(startTime),
            endTime: fromApiTime(endTime),
            weekdays: [],
            windowIds: {},
         }
         const scheduleWeekday = API_WEEKDAYS[weekday]

         schedule.weekdays.push(scheduleWeekday)
         schedule.windowIds[scheduleWeekday] = id
         schedulesByTime.set(timeKey, schedule)
      })

      return [...schedulesByTime.values()]
   })

// One window per weekday; ids kept only for this alarm schedule's windows.
export const getLevelWindows = (
   schedules: OpenCellSchedule[],
   levelId: string,
   alarmSchedule?: AlarmScheduleResponse
): AlarmScheduleWindow[] => {
   const ownWindowIds = alarmSchedule?.windows.map(({ id }) => id) ?? []

   return schedules
      .filter((schedule) => schedule.levelId === levelId)
      .flatMap(({ weekdays, windowIds, startTime, endTime }) =>
         weekdays.map((weekday) => {
            const id = windowIds[weekday]

            return {
               ...(id && ownWindowIds.includes(id) && { id }),
               weekday: API_WEEKDAYS.indexOf(weekday),
               startTime,
               endTime,
            }
         })
      )
}

type ScheduleTime = Pick<OpenCellSchedule, "startTime" | "endTime" | "weekdays">

// Whether the time range or the weekdays differ; weekday order doesn't count.
export const hasScheduleTimeChanged = (
   previous: ScheduleTime,
   next: ScheduleTime
): boolean =>
   previous.startTime !== next.startTime ||
   previous.endTime !== next.endTime ||
   previous.weekdays.length !== next.weekdays.length ||
   previous.weekdays.some((weekday) => !next.weekdays.includes(weekday))

const toApiWeekday = (dayjsDay: number): Weekday =>
   API_WEEKDAYS[(dayjsDay + 6) % 7]

export const getScheduleTimezone = (timezone?: string | null): string =>
   timezone && isValidTimezone(timezone) ? timezone : "UTC"

export const isScheduleActive = (
   schedule: ScheduleTime,
   now: Dayjs
): boolean => {
   const time = now.format(TIME_FORMAT)

   return (
      schedule.weekdays.includes(toApiWeekday(now.day())) &&
      schedule.startTime <= time &&
      time < schedule.endTime
   )
}

type LevelDisplay = Pick<OpenCellScheduleRow, "levelName" | "levelIcon">

// Only floors can hold schedules; built once per levels change.
export const getFloorDisplayById = (
   levelTypes: FacilityDetails["levelTypes"],
   levels: FacilityLevels
): Map<string, LevelDisplay> => {
   const selectors = buildLevelSelectors(levelTypes, levels)
   const floorTypeId = selectors[selectors.length - 1]?.typeId

   return new Map(
      levels
         .filter((level) => level.type.id === floorTypeId)
         .map((floor) => {
            const { levelName, levelIcon } = getLevelSelectorsState(
               levelTypes,
               levels,
               floor.id
            )
            return [floor.id, { levelName, levelIcon }]
         })
   )
}

export const toScheduleRows = (
   alarmSchedules: AlarmScheduleResponse[],
   floorDisplayById: Map<string, LevelDisplay>
): OpenCellScheduleRow[] =>
   schedulesFromAlarmSchedules(alarmSchedules).map((schedule) => ({
      ...schedule,
      levelName: "",
      ...floorDisplayById.get(schedule.levelId),
   }))
